import { useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'

import ConfirmEnterpriseCreationDialog from './ConfirmEnterpriseCreationDialog'
import {
  billsNotetaker,
  buildBillingInfo,
  defaultFormValues,
  type EnterpriseFormValues,
  enterpriseSchema,
  formatCustomSeatPricePreview,
  getPlanLabel,
  isEnterprisePlan,
  isYearly,
  PAYMENT_TERMS_OPTIONS,
  PLAN_OPTIONS,
  planOf,
  PLATFORM_FEE_OPTIONS,
  requiresState,
  toAdminPayload,
  toSalesLedCustomBillingPayload,
  toSalesLedPayload,
} from './schema'

import formFieldStyles from '@/components/FormField/styles.module.scss'
import styles from '@/pages/app/Enterprises/styles.module.scss'

import { isApiError } from '@/api'
import {
  createEnterpriseWithAdmin,
  createSalesLedEnterprise,
  createSalesLedEnterpriseCustomBilling,
} from '@/api/internal'
import Button from '@/components/Button'
import CollapsibleSection from '@/components/CollapsibleSection'
import FormField from '@/components/FormField/FormField'
import Select from '@/components/Select'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import CreateSalesOwnedEnterprise from '@/pages/app/Enterprises/components/CreateSalesOwnedEnterprise'

const LegacyEnterpriseForm = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<EnterpriseFormValues | null>(null)
  const [billingExpanded, setBillingExpanded] = useState(false)
  const [pricingExpanded, setPricingExpanded] = useState(true)
  const { updateToast } = useToast()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EnterpriseFormValues>({
    resolver: zodResolver(enterpriseSchema),
    defaultValues: defaultFormValues,
  })

  // Watch the toggles so we can show/hide the sales-led + new-user fieldsets.
  // Crucially the whole form keeps a single useForm instance, so flipping a
  // toggle no longer wipes the shared email/domain/name fields.
  const isSalesLed = useWatch({ control, name: 'isSalesLed' })
  const createAdminUserIfMissing = useWatch({ control, name: 'createAdminUserIfMissing' })
  const customPricing = useWatch({ control, name: 'customPricing' })
  const plan = useWatch({ control, name: 'plan' })
  const enterprisePlan = isEnterprisePlan(plan)

  const yearly = isYearly(planOf(plan))
  const previousYearly = useRef(yearly)

  useEffect(() => {
    if (enterprisePlan) setValue('customPricing', true)
  }, [enterprisePlan, setValue])

  // A cents amount means something different per billing period; never carry it across a cadence change.
  useEffect(() => {
    if (previousYearly.current !== yearly) {
      previousYearly.current = yearly
      setValue('customUnitPriceCents', '')
    }
  }, [yearly, setValue])
  const customUnitPriceCents = useWatch({ control, name: 'customUnitPriceCents' })
  const country = useWatch({ control, name: 'country' })
  const stateRequired = isSalesLed && requiresState(country)
  const customPricePeriodLabel = isYearly(planOf(plan)) ? 'year' : 'month'
  const customPricePreview = formatCustomSeatPricePreview(Number(customUnitPriceCents), planOf(plan))

  const createMutation = useMutation({
    mutationFn: async (formData: EnterpriseFormValues): Promise<string | undefined> => {
      if (formData.isSalesLed) {
        const customBillingPayload = toSalesLedCustomBillingPayload(formData)
        const response = customBillingPayload
          ? await createSalesLedEnterpriseCustomBilling(customBillingPayload)
          : await createSalesLedEnterprise(toSalesLedPayload(formData))
        return response.data.message
      }
      await createEnterpriseWithAdmin(toAdminPayload(formData))
      return undefined
    },
    onSuccess: (message, formData) => {
      updateToast(
        message ?? (formData.isSalesLed ? 'Enterprise created with draft invoice' : 'Enterprise created successfully'),
        'success'
      )
      reset()
      setPendingFormData(null)
      setShowConfirmDialog(false)
    },
    onError: (err) => {
      const detail = isApiError(err) ? err.detail : undefined
      updateToast(detail ?? 'Error creating enterprise', 'error')
    },
  })
  const isLoading = createMutation.isPending

  const handleConfirm = () => {
    if (!pendingFormData || createMutation.isPending) return
    createMutation.mutate(pendingFormData)
  }

  const handleCancelCreate = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  const handleCreateEnterprise = handleSubmit((data) => {
    setPendingFormData(data)
    setShowConfirmDialog(true)
  })

  const getPlatformFeeLabel = (value: string) => PLATFORM_FEE_OPTIONS.find((o) => o.value === value)?.label ?? value

  return (
    <div>
      {showConfirmDialog && pendingFormData && (
        <ConfirmEnterpriseCreationDialog
          isOpen={showConfirmDialog}
          formData={pendingFormData}
          isLoading={isLoading}
          onConfirm={handleConfirm}
          onCancel={handleCancelCreate}
          salesLedDetails={
            pendingFormData.isSalesLed
              ? {
                  plan: getPlanLabel(pendingFormData.plan),
                  billsNotetaker: billsNotetaker(pendingFormData.plan),
                  seats: pendingFormData.seats,
                  monthlyPlatformFee: getPlatformFeeLabel(pendingFormData.monthlyPlatformFee),
                }
              : undefined
          }
          customPricing={
            pendingFormData.isSalesLed && pendingFormData.customPricing
              ? {
                  unitPriceCents: Number(pendingFormData.customUnitPriceCents),
                  pricePeriod: isYearly(planOf(pendingFormData.plan)) ? 'year' : 'month',
                  preview: formatCustomSeatPricePreview(
                    Number(pendingFormData.customUnitPriceCents),
                    planOf(pendingFormData.plan)
                  ),
                }
              : undefined
          }
          billingInfo={buildBillingInfo(pendingFormData)}
          adminProvisioning={
            pendingFormData.createAdminUserIfMissing
              ? {
                  firstName: pendingFormData.adminFirstName?.trim() ?? '',
                  lastName: pendingFormData.adminLastName?.trim() ?? '',
                }
              : undefined
          }
        />
      )}

      <form className={styles.formSection} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.formFields}>
          <h2 className={styles.sectionTitle}>
            {isSalesLed ? 'New enterprise with Stripe (deprecated)' : 'New Enterprise'}
          </h2>

          <p className={styles.formDescription}>
            {isSalesLed
              ? 'Creates an enterprise with a Stripe subscription and draft invoice. You can add a PO number and send the invoice later from the Manage tab.'
              : 'Creates a new enterprise account and assigns the specified user as admin. No billing is set up.'}
          </p>

          <Controller
            name="isSalesLed"
            control={control}
            render={({ field }) => (
              <div className={styles.toggleRow}>
                <Switch
                  checked={field.value}
                  onInputChange={(next) => {
                    field.onChange(next)
                    // Auto-expand Billing Information when sales-led turns on — its
                    // fields are required there. Toggling off leaves the section as
                    // the user had it; collapsing on their behalf would be jarring.
                    if (next) setBillingExpanded(true)
                  }}
                />
                <div className={styles.toggleLabelGroup}>
                  <span className={styles.toggleLabel}>Sales-led with Stripe (deprecated)</span>
                  <span className={styles.toggleHint}>
                    Generates a Stripe subscription and draft invoice; requires billing contact and address below.
                  </span>
                </div>
              </div>
            )}
          />

          <FormField
            name="email"
            label="Admin Email"
            type="email"
            control={control}
            error={errors.email}
            placeholder="contact@company.com"
          />

          <Controller
            name="createAdminUserIfMissing"
            control={control}
            render={({ field }) => (
              <div className={styles.toggleRow}>
                <Switch checked={field.value} onInputChange={field.onChange} />
                <div className={styles.toggleLabelGroup}>
                  <span className={styles.toggleLabel}>This user doesn&apos;t exist yet — create them</span>
                  <span className={styles.toggleHint}>
                    Provisions a new Wispr account for the admin email above and sends a password-reset email.
                  </span>
                </div>
              </div>
            )}
          />

          {createAdminUserIfMissing && (
            <>
              <FormField
                name="adminFirstName"
                label="Admin First Name"
                control={control}
                error={errors.adminFirstName}
                placeholder="Jane"
              />
              <FormField
                name="adminLastName"
                label="Admin Last Name"
                control={control}
                error={errors.adminLastName}
                placeholder="Doe"
              />
            </>
          )}

          <FormField name="domain" label="Domain" control={control} error={errors.domain} placeholder="company.com" />

          <FormField
            name="name"
            label="Enterprise Name"
            control={control}
            error={errors.name}
            placeholder="Acme Corporation"
          />

          {isSalesLed && (
            <CollapsibleSection
              label="Pricing"
              summary={customPricing ? 'Custom pricing' : 'Preset plan'}
              expanded={pricingExpanded}
              onToggle={() => setPricingExpanded(!pricingExpanded)}
            >
              <Controller
                name="customPricing"
                control={control}
                render={({ field }) => (
                  <div className={styles.toggleRow}>
                    <Switch checked={field.value} onInputChange={field.onChange} disabled={enterprisePlan} />
                    <div className={styles.toggleLabelGroup}>
                      <span className={styles.toggleLabel}>Custom pricing</span>
                      <span className={styles.toggleHint}>
                        Bills seats at a negotiated rate instead of the plan&apos;s list price. Always on for Enterprise
                        plans.
                      </span>
                    </div>
                  </div>
                )}
              />

              <Controller
                name="plan"
                control={control}
                render={({ field }) => (
                  <div className={formFieldStyles.formGroup}>
                    <label className={formFieldStyles.label}>Plan *</label>
                    <Select
                      value={field.value}
                      options={PLAN_OPTIONS}
                      onChange={field.onChange}
                      className={styles.fullWidthSelect}
                    />
                    <span className={formFieldStyles.hint}>
                      {!enterprisePlan
                        ? 'Existing customers: a plan change at renewal is done by engineering, not this form.'
                        : billsNotetaker(plan)
                          ? 'Priced per deal. Notetaker is switched on for the customer automatically when the deal is created.'
                          : 'Priced per deal.'}
                    </span>
                    {errors.plan && <span className={formFieldStyles.errorText}>{errors.plan.message}</span>}
                  </div>
                )}
              />

              <FormField
                name="seats"
                label="Seats"
                type="number"
                control={control}
                error={errors.seats}
                placeholder="1"
              />

              <Controller
                name="monthlyPlatformFee"
                control={control}
                render={({ field }) => (
                  <div className={formFieldStyles.formGroup}>
                    <label className={formFieldStyles.label}>Platform fee</label>
                    <Select
                      value={field.value}
                      options={PLATFORM_FEE_OPTIONS}
                      onChange={field.onChange}
                      className={styles.fullWidthSelect}
                    />
                  </div>
                )}
              />

              <Controller
                name="daysUntilDue"
                control={control}
                render={({ field }) => (
                  <div className={formFieldStyles.formGroup}>
                    <label className={formFieldStyles.label}>Payment Terms</label>
                    <Select
                      value={String(field.value)}
                      options={PAYMENT_TERMS_OPTIONS}
                      onChange={(val) => field.onChange(parseInt(val, 10))}
                      className={styles.fullWidthSelect}
                    />
                  </div>
                )}
              />

              <Controller
                name="preserveContractedSeats"
                control={control}
                render={({ field }) => (
                  <div className={styles.toggleRow}>
                    <Switch checked={field.value} onInputChange={field.onChange} />
                    <div className={styles.toggleLabelGroup}>
                      <span className={styles.toggleLabel}>Preserve contracted seats</span>
                      <span className={styles.toggleHint}>
                        Prevents automatic seat reduction when members are removed
                      </span>
                    </div>
                  </div>
                )}
              />

              {customPricing && (
                <>
                  <FormField
                    name="customUnitPriceCents"
                    label={`Custom Unit Price (USD cents per seat/${customPricePeriodLabel})`}
                    type="number"
                    control={control}
                    error={errors.customUnitPriceCents}
                    placeholder="e.g. 19200"
                    hint="Per seat, in cents, for the plan's billing period."
                  />
                  {customPricePreview && <span className={styles.fieldPreview}>{customPricePreview}</span>}
                </>
              )}
            </CollapsibleSection>
          )}

          <CollapsibleSection
            label="Billing Information"
            summary={isSalesLed ? 'Required' : 'Optional'}
            expanded={billingExpanded}
            onToggle={() => setBillingExpanded(!billingExpanded)}
          >
            <p className={styles.formDescription}>
              Stripe sends invoices and receipts to the billing contact email and shows the address on the invoice.
              {isSalesLed ? ' All fields below are required for sales-led enterprises.' : ''}
            </p>

            <FormField
              name="billingContact"
              label="Billing Contact"
              type="email"
              control={control}
              error={errors.billingContact}
              placeholder="ap@company.com"
              required={isSalesLed}
              hint="Email address that receives Stripe invoices and receipts."
            />

            <FormField
              name="line1"
              label="Address Line 1"
              control={control}
              error={errors.line1}
              placeholder="123 Main St"
              required={isSalesLed}
            />

            <FormField
              name="line2"
              label="Address Line 2"
              control={control}
              error={errors.line2}
              placeholder="Suite 400"
              required={false}
            />

            <FormField
              name="city"
              label="City"
              control={control}
              error={errors.city}
              placeholder="San Francisco"
              required={isSalesLed}
            />

            <FormField
              name="state"
              label="State / Region"
              control={control}
              error={errors.state}
              placeholder="CA"
              required={stateRequired}
              hint={isSalesLed && !stateRequired ? 'Required only for US and Canada.' : undefined}
            />

            <FormField
              name="postalCode"
              label="Postal Code"
              control={control}
              error={errors.postalCode}
              placeholder="94103"
              required={isSalesLed}
            />

            <FormField
              name="country"
              label="Country"
              control={control}
              error={errors.country}
              placeholder="US"
              required={isSalesLed}
              hint="ISO 3166-1 alpha-2 (e.g. US, GB, CA)."
            />
          </CollapsibleSection>

          {!isSalesLed && (
            <Controller
              name="preserveContractedSeats"
              control={control}
              render={({ field }) => (
                <div className={styles.toggleRow}>
                  <Switch checked={field.value} onInputChange={field.onChange} />
                  <div className={styles.toggleLabelGroup}>
                    <span className={styles.toggleLabel}>Preserve contracted seats</span>
                    <span className={styles.toggleHint}>
                      Prevents automatic seat reduction when members are removed
                    </span>
                  </div>
                </div>
              )}
            />
          )}

          <Button
            title={isSalesLed ? 'Create Enterprise with Subscription' : 'Create Enterprise'}
            onClick={handleCreateEnterprise}
            type="dark"
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </div>
      </form>
    </div>
  )
}

const CreateEnterprise = () => {
  const [isLegacyExpanded, setIsLegacyExpanded] = useState(false)

  return (
    <>
      <CreateSalesOwnedEnterprise />
      <CollapsibleSection
        label="Other provisioning options"
        summary="Legacy Stripe provisioning is deprecated"
        description="For new sales-led contracts, use the sales-owned form above. Creating an enterprise without billing is still supported here."
        expanded={isLegacyExpanded}
        onToggle={() => setIsLegacyExpanded(!isLegacyExpanded)}
      >
        <LegacyEnterpriseForm />
      </CollapsibleSection>
    </>
  )
}

export default CreateEnterprise
